import React from 'react';
import { Booking } from 'acscp/store';
import { FormattedDate } from 'acscp/components/common/formattedDate';
import { LegTable } from 'acscp/components/legs/legTable';
import { FlightPlan } from 'acscp/components/legs/flightPlan/flightPlan';
import { SingleLineFlightPlanTrack } from 'acscp/components/legs/flightPlan/singleLineFlightPlanTrack';
import { TextOnlyFlightPlan } from 'acscp/components/legs/flightPlan/textOnlyFlightPlan';
import { FlightStartEndDates } from 'acscp/components/legs/flightStartEndDates';
import style from './flightRequest.module.scss';
import { FormattedMessage } from 'react-intl';
import { PageTitle } from 'acscp/components/common/pageTitle';
import { Row } from 'acscp/components/common/row';
import { MoreLessText } from '../common/moreLessText';
import { ButtonRow } from '../common/buttonRow';
import classNames from 'classnames';
import { Department } from 'acscp/api/models/customTypes/department';
import { AcmiEnquiryDetails } from 'acscp/components/acmiEnquiryDetails/acmiEnquiryDetails';
import { AcmiFlightTrackRow } from 'acscp/components/legs/flightPlan/acmiFlightTrackRow';

interface FlightRequestOwnProps {
    bookingDetails: Booking;
    quoteCount: number | null;
    title: JSX.Element;
    shouldShowFlightPlan: boolean;
}

const FlightRequest: React.SFC<FlightRequestOwnProps> = ({
    bookingDetails,
    quoteCount,
    title,
    shouldShowFlightPlan,
}) => {
    const {
        enquiryId: refNumber,
        legs,
        jetCardContractDate,
        jetCardNo,
    } = bookingDetails;
    // jetCardNo might be set to the empty string by IWAS
    const isJetCardHolder = jetCardNo !== null && jetCardNo !== '';
    const departmentKey = bookingDetails.departmentKey;
    return (
        <div
            className={classNames(style.FlightRequest, {[style.PaddingBottomLarge]: !shouldShowFlightPlan})}
        >
            <PageTitle
                title={
                    <div className={style.PageTitle}>
                        <span className={style.PageTitleText}>{title}</span>
                        <div className={style.PageTitleContents}>
                            <div className={style.ReferenceSection}>
                                <div className={style.ReferenceLabel}>
                                    {bookingDetails.isInAcmiWorkflow ? (
                                        <>
                                            <FormattedMessage
                                                id={'components.flightRequest.enquiryId'}
                                                defaultMessage={'ENQUIRY ID'}
                                            />:
                                        </>
                                    ) : (
                                        <>
                                            <FormattedMessage
                                                id={'components.flightRequest.reference'}
                                                defaultMessage={'REFERENCE'}
                                            />:
                                        </>
                                    )}
                                </div>
                                <div className={style.ReferenceNumber}>{refNumber}</div>
                            </div>
                            {bookingDetails.isInAcmiWorkflow && (
                                <div className={style.LeaseTypeSection}>
                                    <div className={style.ReferenceLabel}>
                                        <FormattedMessage
                                            id={'components.flightRequest.leaseType'}
                                            defaultMessage={'LEASE TYPE'}
                                        />:
                                    </div>
                                    <div className={style.ReferenceNumber}>{bookingDetails.leaseType}</div>
                                </div>
                            )}
                            {isJetCardHolder && (
                                <>
                                    <div className={style.MasterContractDateSection}>
                                        <div className={style.ReferenceLabel}>
                                            <FormattedMessage
                                                id={'components.flightRequest.masterContactDate'}
                                                defaultMessage={'MASTER CONTRACT DATE'}
                                            />:
                                        </div>
                                        <div className={style.ReferenceNumber}>
                                            <FormattedDate date={jetCardContractDate!}/>
                                        </div>
                                    </div>
                                    <div className={style.JetCardNumberSection}>
                                        <div className={style.ReferenceLabel}>
                                            <FormattedMessage
                                                id={'components.flightRequest.jetCardNo'}
                                                defaultMessage={'JET CARD NUMBER'}
                                            />:
                                        </div>
                                        <div className={style.ReferenceNumber}>{jetCardNo}</div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>}
            >
                <ButtonRow bookingDetails={bookingDetails} narrow={true}/>
            </PageTitle>

            <Row>
                <ButtonRow bookingDetails={bookingDetails} className={style.MobileSecondBar}>
                    <div>
                        <div className={classNames(style.ReferenceNumber, style.InlineRef)}>{refNumber}</div>
                    </div>
                </ButtonRow>
            </Row>
            {departmentKey === Department.ACMI ? (
                <AcmiFlightTrackRow legs={legs} quoteCount={quoteCount}/>
                ) : (
                    <>
                        <Row
                            className={shouldShowFlightPlan
                                ? style.FlightStartEndDates
                                : style.QuotesAvailable}
                        >
                            {shouldShowFlightPlan && <FlightStartEndDates legs={legs}/>}
                            {quoteCount !== null &&
                                <div className={style.Count}>
                                    <span className={style.LabelLarge}>
                                        {departmentKey === Department.COMMJ
                                            ?
                                            <FormattedMessage
                                               id={'components.flightRequest.optionsAvailable'}
                                               defaultMessage={'OPTIONS AVAILABLE'}
                                            />
                                            :
                                            <FormattedMessage
                                               id={'components.flightRequest.quotesAvailable'}
                                               defaultMessage={'QUOTES AVAILABLE'}
                                            />
                                        }{': '}
                                    </span>
                                    <span className={style.LabelMedium}>
                                    {departmentKey === Department.COMMJ
                                        ?
                                        <FormattedMessage
                                           id={'components.flightRequest.options'}
                                           defaultMessage={'OPTIONS'}
                                        />
                                        :
                                        <FormattedMessage
                                           id={'components.flightRequest.quotes'}
                                           defaultMessage={'QUOTES'}
                                        />}{': '}
                                    </span>
                                    <span>{quoteCount}</span>
                                </div>
                            }
                        </Row>
                        {shouldShowFlightPlan &&
                            <>
                                <Row>
                                    <FlightPlan
                                        desktopFlightPlan={
                                            <SingleLineFlightPlanTrack
                                                legs={legs}
                                                withDurations={false}
                                                withBackground={true}
                                            />
                                        }
                                        mobileFlightPlan={
                                            <TextOnlyFlightPlan
                                                legs={legs}
                                                withLogo={true}
                                            />
                                        }
                                    />
                                </Row>
                            </>
                        }
                    </>
            )}
            {shouldShowFlightPlan &&
                <>
                    <Row>
                        {bookingDetails.isInAcmiWorkflow ? (
                                <>
                                    <div className={style.AcmiNotesToCustomer}>
                                        <FormattedMessage
                                            id={'components.flightRequest.acmiOfferNotes'}
                                            defaultMessage={'As per your recent request, ACS Leasing are pleased to ' +
                                                'be able to offer the following aircraft, subject to prior sales /' +
                                                ' crew / maintenance:'}
                                        />
                                    </div>
                                    <AcmiEnquiryDetails bookingDetails={bookingDetails}/>
                                    <div className={style.AcmiNotesToCustomer}>
                                        <FormattedMessage
                                            id={'components.flightRequest.acmiOutlineNotes'}
                                            defaultMessage={'Standard ACMI Exclusions apply of which will be' +
                                                ' highlighted within the aircraft lease agreement, but a brief' +
                                                ' outline are as follows but not limited to:'}
                                        />
                                    </div>
                                </>
                            ) : <LegTable legs={legs} departmentKey={departmentKey}/>
                        }
                    </Row>
                    {bookingDetails.htmlNotesToCustomer &&
                        <Row>
                            <div className={style.NotesToCustomer}>
                                <MoreLessText unsafeHTML={bookingDetails.htmlNotesToCustomer} minimumLines={7}/>
                            </div>
                        </Row>
                    }
                    {bookingDetails.notesToCustomer && !bookingDetails.htmlNotesToCustomer &&
                        <Row>
                            <div className={style.NotesToCustomer}>
                                <MoreLessText text={bookingDetails.notesToCustomer} minimumLines={7}/>
                            </div>
                        </Row>
                    }
                </>
            }
        </div>
    );
};

export { FlightRequest };
